/**
 * PaymentsStatusPanel (Mode B)
 *
 * Shown on the Lease Detail Payments tab once `paymentMethod !== 'none'` (payments enabled).
 * Leads with an at-a-glance status summary (AC-4/AC-9), hoists Recurring Charges above the
 * settings editors with a per-period total (AC-5), then hosts the same four EXISTING editors
 * (PaymentMethodCard / CollectionSetupCard / PaymentTermsCard / LateFeeConfigSection) as a
 * collapsed-by-default, one-row-open-at-a-time accordion (AC-6), each showing its current value
 * while collapsed. PaymentActivationCard and (external mode) TenantAliasesPanel follow, in their
 * existing relative order — see the change-19 design (state-aware Payments tab).
 *
 * Deviation from the literal design prop list (flagged per task instructions): the design's
 * props enumeration for this component omits `selectedMethod`, `rentAmount`, `tenants` and
 * `leaseId`, but the design also requires this component to (a) host PaymentMethodCard /
 * CollectionSetupCard / PaymentActivationCard, all of which need `selectedMethod` exactly like
 * PaymentsSetupChecklist already receives it; (b) display "rent amount" in the status summary
 * (AC-4), which is not derivable from any of the listed props (rent lives on the Lease, not
 * PaymentConfig or LeaseChargeConfig); and (c) retain the existing TenantAliasesPanel block,
 * which needs a tenant list and a leaseId (see the pre-existing PaymentSettingsTab). These four
 * props were added as the minimal wiring needed to satisfy those requirements without changing
 * any business rule.
 */

import { useRef, useState, useEffect } from 'react';
import { ChevronRight, Check } from 'lucide-react';
import {
  type PaymentConfig,
  type UpdatePaymentConfigInput,
  type LeaseChargeConfig,
  type CreateChargeConfigInput,
  type UpdateChargeConfigInput,
  type LandlordFundingSource,
  type PropertyBankInfo,
  type PaymentReadiness,
} from '../../lib/api';
import type { LinkedBankAccount } from '../../api/linked-bank-accounts.api';
import type { LeaseState } from '../../types/lease';
import type { MethodSelection } from './payment-helpers';
import { PaymentMethodCard, CollectionSetupCard, PaymentTermsCard, PaymentActivationCard } from './PaymentSettingsForm';
import { LateFeeConfigSection } from './LateFeeConfigSection';
import { RecurringChargesSection } from './RecurringChargesSection';
import { TenantAliasesPanel } from '../TenantAliasesPanel';
import { PageSection, DataGrid, DataRow } from '../layout/index';

type RowKey = 'method' | 'collection' | 'terms' | 'late-fees';

// ?focus= deep-link values (shared with Mode A / PaymentsSetupChecklist) mapped onto this
// accordion's row keys. 'activate' targets the PaymentActivationCard below the accordion, not a
// row inside it.
const FOCUS_TO_ROW: Record<string, RowKey> = {
  deposit: 'collection',
  monitoring: 'collection',
  terms: 'terms',
  'late-fees': 'late-fees',
};

interface PaymentsStatusPanelProps {
  paymentConfig: PaymentConfig | null;
  chargeConfigs: LeaseChargeConfig[];
  readiness: PaymentReadiness;
  fundingSources: LandlordFundingSource[];
  propertyBankInfo: PropertyBankInfo | null;
  linkedBankAccounts: LinkedBankAccount[];
  onUpdateConfig: (data: UpdatePaymentConfigInput) => Promise<boolean>;
  onMethodChange: (method: MethodSelection) => void;
  onCreateCharge: (data: CreateChargeConfigInput) => Promise<void>;
  onUpdateCharge: (chargeConfigId: string, data: UpdateChargeConfigInput) => Promise<void>;
  onDeleteCharge: (chargeConfigId: string) => void;
  onSetPropertyDefault: (fundingSourceId: string | null) => void;
  onSetMonitoringAccount: (linkedBankAccountId: string) => void;
  /** ?focus= deep-link value (deposit | terms | late-fees | monitoring | activate). */
  focusSection?: string | null;
  // See file-level deviation note: added beyond the design's literal prop list.
  selectedMethod: MethodSelection;
  rentAmount: number;
  tenants: Array<{ id: string; firstName: string; lastName: string; email: string; phone?: string | null }>;
  leaseId: string;
  /** BR-LL-10/BR-LL-11 — a lease past `active` no longer has automatic collection running; the
   *  status summary must say so instead of unconditionally claiming "active". */
  leaseState?: LeaseState;
}

export function PaymentsStatusPanel({
  paymentConfig,
  chargeConfigs,
  readiness,
  fundingSources,
  propertyBankInfo,
  linkedBankAccounts,
  onUpdateConfig,
  onMethodChange,
  onCreateCharge,
  onUpdateCharge,
  onDeleteCharge,
  onSetPropertyDefault,
  onSetMonitoringAccount,
  focusSection,
  selectedMethod,
  rentAmount,
  tenants,
  leaseId,
  leaseState,
}: PaymentsStatusPanelProps) {
  const wantsAch = selectedMethod === 'ach';
  const wantsExternal = selectedMethod === 'external';

  const accordionRef = useRef<HTMLDivElement>(null);
  const [manualRow, setManualRow] = useState<RowKey | null>(null);
  const focusRow = focusSection ? FOCUS_TO_ROW[focusSection] : undefined;

  // A focus deep-link wins the moment it arrives and stays applied (via manualRow) once the URL
  // param is stripped by the parent's scroll/highlight effect.
  useEffect(() => {
    if (focusRow) setManualRow(focusRow);
  }, [focusRow]);

  const expandedRow: RowKey | null = focusRow ?? manualRow;

  function handleToggleRow(row: RowKey) {
    setManualRow((prev) => (prev === row ? null : row));
  }

  function handleManage() {
    setManualRow('method');
    accordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const activeCharges = chargeConfigs.filter((c) => c && c.isActive);
  const recurringTotal = activeCharges.reduce((sum, c) => sum + c.amount, 0);

  const destinationLabel = wantsAch ? 'Deposit account' : 'Monitoring account';
  const destinationSummary = describeAccount(wantsAch, wantsExternal, paymentConfig, propertyBankInfo, fundingSources);
  const methodDone = selectedMethod !== 'none';
  const accountDone = wantsAch ? readiness.ach.ready : wantsExternal ? readiness.external.ready : false;
  const termsDone = paymentConfig?.termsSetAt != null;
  const lateFeesDone = paymentConfig?.lateFeesSetAt != null;
  const accountIssues = wantsAch ? readiness.ach.issues : wantsExternal ? readiness.external.issues : [];
  const accountNeedsAttention = accountIssues.some((i) => i.severity === 'fatal');

  const termsSummary = termsDone && paymentConfig
    ? `Due ${paymentConfig.rentDueDay}${getDaySuffix(paymentConfig.rentDueDay)} · ${paymentConfig.gracePeriodDays}-day grace`
    : 'Not set';
  const lateFeeSummary = describeLateFee(paymentConfig);

  // BR-LL-10: tenancy end stops AUTOMATIC collection, not settlement. BR-LL-11: archive is
  // read-only — nothing (automatic or manual) runs anymore. Only 'active'/'scheduled'/'ending'
  // still have live collection; the dot/copy must reflect that instead of always saying "active".
  const collectionStatus =
    leaseState === 'archived'
      ? { dotClass: 'bg-muted-foreground', label: 'Rent collection stopped — lease archived' }
      : leaseState === 'ended'
        ? { dotClass: 'bg-warning', label: 'Automatic collection stopped — settlement continues' }
        : { dotClass: 'bg-success', label: 'Rent collection is active' };

  return (
    <div className="flex flex-col gap-4">
      {/* a) Status summary */}
      <div className="bg-card rounded-lg border border-border p-4 sm:p-6" data-testid="payments-status-summary">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full shrink-0 ${collectionStatus.dotClass}`} aria-hidden="true" />
              <h3 className="text-lg font-semibold text-foreground m-0 truncate">
                {wantsAch ? 'ACH / Bank Transfer' : wantsExternal ? 'External (Zelle / Cash)' : 'Payments'}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5 mb-0" data-testid="payments-collection-status">
              {collectionStatus.label}
            </p>
          </div>
          <button
            type="button"
            data-testid="payments-manage-btn"
            onClick={handleManage}
            className="shrink-0 px-3 py-2 text-sm font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 min-h-[44px]"
          >
            Manage
          </button>
        </div>
        <DataGrid columns={2}>
          <DataRow label="Rent amount" value={formatCurrency(rentAmount)} />
          {paymentConfig && (
            <>
              <DataRow label="Due day" value={`${paymentConfig.rentDueDay}${getDaySuffix(paymentConfig.rentDueDay)}`} />
              <DataRow
                label="Grace period"
                value={`${paymentConfig.gracePeriodDays} day${paymentConfig.gracePeriodDays === 1 ? '' : 's'}`}
              />
              <DataRow label="Late fee" value={lateFeeSummary} />
            </>
          )}
          <DataRow label={destinationLabel} value={destinationSummary} />
        </DataGrid>
      </div>

      {/* b) Recurring Charges — hoisted above the settings, with a per-period total */}
      <div className="flex flex-col gap-2">
        <div
          className="bg-card rounded-lg border border-border p-4 flex items-center justify-between"
          data-testid="recurring-charges-total"
        >
          <span className="text-sm font-medium text-muted-foreground">Recurring charges total</span>
          <span className="text-sm font-semibold text-foreground">{formatCurrency(recurringTotal)}/mo</span>
        </div>
        <RecurringChargesSection
          chargeConfigs={chargeConfigs}
          onCreate={onCreateCharge}
          onUpdate={onUpdateCharge}
          onDelete={onDeleteCharge}
        />
      </div>

      {/* c) Four-row settings accordion */}
      <div ref={accordionRef} data-testid="payments-settings-accordion" className="flex flex-col gap-2">
        <SettingsRow
          rowKey="method"
          title="Payment method"
          summary={describeMethod(selectedMethod)}
          complete={methodDone}
          needsAttention={false}
          expanded={expandedRow === 'method'}
          onToggle={() => handleToggleRow('method')}
        >
          <PaymentMethodCard selectedMethod={selectedMethod} onMethodChange={onMethodChange} />
        </SettingsRow>

        <SettingsRow
          rowKey="collection"
          title="Collection setup"
          summary={destinationSummary}
          complete={accountDone}
          needsAttention={accountNeedsAttention}
          expanded={expandedRow === 'collection'}
          onToggle={() => handleToggleRow('collection')}
        >
          <CollectionSetupCard
            paymentConfig={paymentConfig}
            selectedMethod={selectedMethod}
            readiness={readiness}
            fundingSources={fundingSources}
            propertyBankInfo={propertyBankInfo}
            linkedBankAccounts={linkedBankAccounts}
            onUpdate={onUpdateConfig}
            onSetPropertyDefault={onSetPropertyDefault}
            onSetMonitoringAccount={onSetMonitoringAccount}
          />
        </SettingsRow>

        <SettingsRow
          rowKey="terms"
          title="Payment terms"
          summary={termsSummary}
          complete={termsDone}
          needsAttention={false}
          expanded={expandedRow === 'terms'}
          onToggle={() => handleToggleRow('terms')}
        >
          <PaymentTermsCard paymentConfig={paymentConfig} onUpdate={onUpdateConfig} />
        </SettingsRow>

        <SettingsRow
          rowKey="late-fees"
          title="Late fees"
          summary={lateFeeSummary}
          complete={lateFeesDone}
          needsAttention={false}
          expanded={expandedRow === 'late-fees'}
          onToggle={() => handleToggleRow('late-fees')}
        >
          <LateFeeConfigSection paymentConfig={paymentConfig} onUpdate={onUpdateConfig} />
        </SettingsRow>
      </div>

      {/* d) Activation + (external) Payment Aliases — same relative order as before */}
      {selectedMethod !== 'none' && (
        <PaymentActivationCard
          paymentConfig={paymentConfig}
          selectedMethod={selectedMethod}
          readiness={readiness}
          onUpdate={onUpdateConfig}
          leaseState={leaseState}
        />
      )}

      {paymentConfig?.paymentMethod === 'external' && tenants.length > 0 && (
        <PageSection title="Payment Aliases" data-testid="tenant-aliases-section">
          <div className="px-[var(--page-padding-x)] md:px-4 pb-3">
            <p className="text-sm text-muted-foreground mb-3">
              Aliases help the system match bank transactions to tenants.
            </p>
            {tenants.map((tenant) => (
              <TenantAliasesPanel key={tenant.id} tenantId={tenant.id} defaultLeaseId={leaseId} />
            ))}
          </div>
        </PageSection>
      )}
    </div>
  );
}

interface SettingsRowProps {
  rowKey: RowKey;
  title: string;
  summary: string;
  complete: boolean;
  needsAttention: boolean;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function SettingsRow({ rowKey, title, summary, complete, needsAttention, expanded, onToggle, children }: SettingsRowProps) {
  return (
    <div
      data-testid={`payments-settings-row-${rowKey}`}
      className={`rounded-lg border overflow-hidden transition-colors ${
        expanded ? 'border-primary' : 'border-border'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`payments-settings-row-${rowKey}-toggle`}
        className={`w-full flex items-center justify-between gap-3 p-4 min-h-[44px] text-left transition-colors ${
          expanded ? 'bg-primary/5' : 'bg-card hover:bg-accent/50'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            aria-hidden="true"
            className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-semibold shrink-0 ${
              complete ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
            }`}
          >
            {complete ? <Check className="h-3.5 w-3.5" /> : null}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">{summary}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {needsAttention && (
            <span
              data-testid={`payments-settings-row-${rowKey}-attention`}
              className="text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded"
            >
              Needs attention
            </span>
          )}
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border bg-background p-4">{children}</div>
      )}
    </div>
  );
}

/* ── Summary helpers — deliberately local duplicates; see PaymentsSetupChecklist.tsx for the
   Mode A twin. The formatters they mirror live unexported in PaymentSettingsForm.tsx, which is
   outside this layer's edit scope for this slice. ── */

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function describeMethod(method: MethodSelection): string {
  if (method === 'ach') return 'ACH / Bank Transfer';
  if (method === 'external') return 'External (Zelle / Cash)';
  return 'Not set';
}

function describeAccount(
  wantsAch: boolean,
  wantsExternal: boolean,
  paymentConfig: PaymentConfig | null,
  propertyBankInfo: PropertyBankInfo | null,
  fundingSources: LandlordFundingSource[]
): string {
  if (wantsAch) {
    const selected = paymentConfig?.destinationId
      ? fundingSources.find((s) => s.id === paymentConfig.destinationId)
      : null;
    if (selected) return formatAccountSummary(selected);
    if (propertyBankInfo?.defaultDepositAccount) {
      return `Property default: ${formatAccountSummary(propertyBankInfo.defaultDepositAccount)}`;
    }
    return 'Not set';
  }
  if (wantsExternal) {
    const mon = propertyBankInfo?.monitoringAccount;
    if (mon) return `${mon.institutionName} ••••${mon.accountMask}`;
    return 'Not set';
  }
  return 'Not set';
}

function formatAccountSummary(account: { bankName: string; accountMask: string; friendlyName: string | null; isPrimary: boolean }): string {
  const details = `${account.bankName} ••••${account.accountMask}`;
  const label = account.friendlyName ? `${account.friendlyName} (${details})` : details;
  return account.isPrimary ? `${label} (Primary)` : label;
}

function describeLateFee(config: PaymentConfig | null): string {
  if (!config || config.lateFeeType == null) return 'No late fee';
  const amount = config.lateFeeAmount != null ? config.lateFeeAmount / 100 : 0;
  if (config.lateFeeType === 'flat') return `Flat $${amount}`;
  if (config.lateFeeType === 'percentage') return `${amount}% of rent`;
  const max = config.lateFeeMaxAmount != null ? config.lateFeeMaxAmount / 100 : null;
  return `$${amount}/day${max != null ? ` up to $${max}` : ''}`;
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}
